import {
  AgreementMappingFlags,
  ConsultaItem,
  ConsultaObject,
  ContratoDetalhado,
  ContratoItem,
  ContratoServicoPlano,
  IntegrationData,
} from "./IntegrationDataDisplay.types";

/**
 * Normaliza os formatos das integrações num modelo único.
 *
 * Cada plataforma devolve um shape diferente, e o backend escolhe uma chave de
 * match diferente para cada uma (ver partiu-api/src/apps/integrations):
 *
 * - IXC     → id_vd_contrato (mapped_by_id) ou contrato (por nome)
 * - MK      → plano_acesso
 * - Adapter → info_plano[].description
 */

export type IntegrationFormat =
  | "ixc"
  | "mk"
  | "adapter"
  | "slugs"
  | "desconhecido";

export interface NormalizedPlan {
  /** Texto comparado com plan_slug_integration */
  name: string;
  /** Id do plano na integração, quando existe */
  id?: string;
  code?: string;
  amount?: number;
  /** Origem legível, para o operador saber de onde saiu */
  source: string;
}

export type ContractIssueKind =
  | "vencido"
  | "cancelado"
  | "suspenso"
  | "inativo"
  | "atraso";

export interface ContractIssue {
  kind: ContractIssueKind;
  detail: string;
}

export interface ContractDate {
  label: string;
  value: string;
  /** true quando a data já passou e isso importa */
  overdue?: boolean;
}

export interface NormalizedContract {
  key: string;
  label: string;
  plans: NormalizedPlan[];
  dates: ContractDate[];
  issues: ContractIssue[];
  status?: string;
}

export interface NormalizedClient {
  name?: string;
  document?: string;
  contact?: string;
  address?: string;
}

export interface NormalizedIntegration {
  format: IntegrationFormat;
  contracts: NormalizedContract[];
  /** Planos que não pertencem a um contrato (combo_slugs etc.) */
  loosePlans: NormalizedPlan[];
  clients: NormalizedClient[];
  /** Problemas de contrato agregados — explicam falha sem ser mapeamento */
  issues: ContractIssue[];
}

/* ------------------------------------------------------------------ */
/* Datas                                                               */
/* ------------------------------------------------------------------ */

/** IXC usa "0000-00-00" como "sem data" — não é data real */
const ZERO_DATE = "0000-00-00";

export function parseIntegrationDate(
  value: string | null | undefined
): Date | undefined {
  if (!value) return undefined;

  const raw = String(value).trim();
  if (!raw || raw.startsWith(ZERO_DATE)) return undefined;

  // "YYYY-MM-DD" ou "YYYY-MM-DD HH:mm:ss"
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const date = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  // "DD/MM/YYYY"
  const br = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (br) {
    const date = new Date(Number(br[3]), Number(br[2]) - 1, Number(br[1]));
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  return undefined;
}

export function formatIntegrationDate(
  value: string | null | undefined
): string | undefined {
  return parseIntegrationDate(value)?.toLocaleDateString("pt-BR");
}

function startOfToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

function isPast(value: string | null | undefined): boolean {
  const date = parseIntegrationDate(value);
  return !!date && date < startOfToday();
}

function pushDate(
  dates: ContractDate[],
  label: string,
  value: string | null | undefined,
  overdue = false
) {
  const formatted = formatIntegrationDate(value);
  if (formatted) dates.push({ label, value: formatted, overdue });
}

const isYes = (value: string | undefined) =>
  String(value || "")
    .trim()
    .toUpperCase() === "S";

/* ------------------------------------------------------------------ */
/* IXC                                                                 */
/* ------------------------------------------------------------------ */

function isIxcConsulta(item: ConsultaItem): boolean {
  return (
    item.id_vd_contrato !== undefined ||
    item.pago_ate_data !== undefined ||
    item.data_ativacao !== undefined ||
    item.status_internet !== undefined
  );
}

function adaptIxcContract(
  item: ConsultaItem,
  index: number,
  flags: AgreementMappingFlags
): NormalizedContract {
  const label = `Contrato ${item.id || index + 1}`;
  const issues: ContractIssue[] = [];
  const dates: ContractDate[] = [];

  pushDate(dates, "Ativado em", item.data_ativacao);
  pushDate(dates, "Pago até", item.pago_ate_data, isPast(item.pago_ate_data));
  pushDate(dates, "Vence em", item.data_expiracao, isPast(item.data_expiracao));
  pushDate(dates, "Renovação", item.data_renovacao);

  if (isPast(item.data_expiracao)) {
    issues.push({
      kind: "vencido",
      detail: `Contrato expirou em ${formatIntegrationDate(
        item.data_expiracao
      )}.`,
    });
  } else if (isPast(item.pago_ate_data)) {
    issues.push({
      kind: "vencido",
      detail: `Contrato pago apenas até ${formatIntegrationDate(
        item.pago_ate_data
      )}.`,
    });
  }

  const cancelledAt = formatIntegrationDate(item.data_cancelamento);
  if (cancelledAt) {
    issues.push({ kind: "cancelado", detail: `Cancelado em ${cancelledAt}.` });
    pushDate(dates, "Cancelado em", item.data_cancelamento, true);
  }

  const quitAt = formatIntegrationDate(item.data_desistencia);
  if (quitAt) {
    issues.push({ kind: "cancelado", detail: `Desistência em ${quitAt}.` });
  }

  const suspendedFrom = formatIntegrationDate(item.data_inicial_suspensao);
  const suspendedTo = formatIntegrationDate(item.data_final_suspensao);
  // contrato_suspenso === "N" é resposta explícita: há data de suspensão
  // registrada mas o contrato NÃO está suspenso agora.
  const notSuspended =
    String(item.contrato_suspenso || "").trim().toUpperCase() === "N";
  if (
    isYes(item.contrato_suspenso) ||
    (!notSuspended && suspendedFrom && !suspendedTo)
  ) {
    issues.push({
      kind: "suspenso",
      detail: suspendedFrom
        ? `Suspenso desde ${suspendedFrom}${
            suspendedTo ? ` até ${suspendedTo}` : " (sem data de retorno)"
          }.`
        : "Contrato marcado como suspenso.",
    });
    pushDate(dates, "Suspenso desde", item.data_inicial_suspensao, true);
  }

  const status = (item.status || "").trim().toUpperCase();
  if (status && status !== "A") {
    issues.push({
      kind: "inativo",
      detail: `Status do contrato no IXC é "${item.status}" (ativo seria "A").`,
    });
  }

  const overdueInstallments = Number(item.num_parcelas_atraso || 0);
  if (overdueInstallments > 0) {
    issues.push({
      kind: "atraso",
      detail: `${overdueInstallments} parcela(s) em atraso.`,
    });
  }

  // O backend escolhe a chave conforme as flags do convênio.
  const name = flags.mappedById
    ? item.id_vd_contrato || item.contrato || ""
    : item.contrato || item.descricao_aux_plano_venda || "";

  const plans: NormalizedPlan[] = [];
  if (name) {
    plans.push({
      name,
      id: item.id_vd_contrato || undefined,
      source: label,
    });
  }
  // O nome também entra quando o mapeamento é por id, para o operador comparar.
  if (flags.mappedById && item.contrato && item.contrato !== name) {
    plans.push({
      name: item.contrato,
      id: item.id_vd_contrato || undefined,
      source: `${label} (nome)`,
    });
  }

  return {
    key: item.id || `ixc-${index}`,
    label,
    plans,
    dates,
    issues,
    status: item.status,
  };
}

/* ------------------------------------------------------------------ */
/* MK                                                                  */
/* ------------------------------------------------------------------ */

function adaptMkContract(item: ContratoItem, index: number): NormalizedContract {
  const label = `Contrato ${item.codcontrato || index + 1}`;
  const issues: ContractIssue[] = [];
  const dates: ContractDate[] = [];

  pushDate(dates, "Adesão", item.adesao);
  pushDate(
    dates,
    "Vence em",
    item.previsao_vencimento,
    isPast(item.previsao_vencimento)
  );

  // O MK descarta contratos vencidos antes de mapear (_map_plan).
  if (isPast(item.previsao_vencimento)) {
    issues.push({
      kind: "vencido",
      detail: `Contrato venceu em ${formatIntegrationDate(
        item.previsao_vencimento
      )} — o MK ignora contratos vencidos ao mapear.`,
    });
  }

  const status = (item.status_contrato || "").trim();
  if (status && !/ativ/i.test(status)) {
    issues.push({
      kind: "inativo",
      detail: `Status do contrato no MK: "${status}".`,
    });
  }

  const name = item.plano_acesso || item.descricao || "";
  const plans: NormalizedPlan[] = name ? [{ name, source: label }] : [];

  return {
    key: item.codcontrato != null ? String(item.codcontrato) : `mk-${index}`,
    label,
    plans,
    dates,
    issues,
    status: item.status_contrato,
  };
}

/* ------------------------------------------------------------------ */
/* Adapter / Voalle-Synsuite                                           */
/* ------------------------------------------------------------------ */

function collectServico(
  servico: ContratoServicoPlano,
  source: string,
  out: NormalizedPlan[]
) {
  if (servico.description) {
    // `id` aqui é o id do serviço-no-contrato (ex.: 1827469), NÃO um id de
    // plano — o Voalle/Synsuite mapeia por description. Usar como chave de id
    // geraria match falso, então fica só o serviceProductCode como código.
    out.push({
      name: servico.description,
      code: servico.serviceProductCode || undefined,
      amount: servico.amount,
      source,
    });
  }

  servico.servicesComposition?.forEach((child) =>
    collectServico(child, `${source} › composição`, out)
  );
  servico.servicesCombination?.forEach((child) =>
    collectServico(child, `${source} › combinação`, out)
  );
}

function formatAddress(
  endereco: ContratoDetalhado["info_endereco"]
): string | undefined {
  if (!endereco) return undefined;
  const street = [endereco.streetType, endereco.street]
    .filter(Boolean)
    .join(" ");
  const parts = [
    street && endereco.number ? `${street}, ${endereco.number}` : street,
    endereco.addressComplement,
    endereco.neighborhood,
    endereco.city && endereco.state
      ? `${endereco.city}/${endereco.state}`
      : endereco.city || endereco.state,
    endereco.postalCode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" - ") : undefined;
}

function joinUnique(values: (string | undefined | null)[]): string | undefined {
  const unique = Array.from(
    new Set(values.map((v) => v?.trim()).filter(Boolean) as string[])
  );
  return unique.length ? unique.join(" / ") : undefined;
}

function adaptAdapterContract(
  contrato: ContratoDetalhado,
  index: number
): { contract: NormalizedContract; client?: NormalizedClient } {
  const label = `Contrato ${index + 1}`;
  const issues: ContractIssue[] = [];
  const dates: ContractDate[] = [];

  pushDate(dates, "Início", contrato.startDate);
  pushDate(dates, "Vence em", contrato.endDate, isPast(contrato.endDate));
  pushDate(dates, "Vencimento", contrato.dueDate, isPast(contrato.dueDate));

  if (isPast(contrato.endDate)) {
    issues.push({
      kind: "vencido",
      detail: `Contrato venceu em ${formatIntegrationDate(contrato.endDate)}.`,
    });
  }

  const status = (contrato.status || "").trim();
  if (status && !/ativ/i.test(status)) {
    issues.push({ kind: "inativo", detail: `Status do contrato: "${status}".` });
  }

  const plans: NormalizedPlan[] = [];
  contrato.info_plano?.forEach((plano) => collectServico(plano, label, plans));

  const cliente = contrato.info_cliente;
  const client: NormalizedClient | undefined = cliente
    ? {
        name: cliente.name,
        document: cliente.clientTxId
          ? `${cliente.typeTxId || "Documento"}: ${cliente.clientTxId}`
          : undefined,
        contact: joinUnique([cliente.phone, cliente.cellPhone1, cliente.email]),
        address: formatAddress(contrato.info_endereco),
      }
    : undefined;

  return {
    contract: {
      key: cliente?.clientTxId || `adapter-${index}`,
      label,
      plans,
      dates,
      issues,
      status: contrato.status,
    },
    client,
  };
}

/* ------------------------------------------------------------------ */

function adaptConsultaObject(consulta: ConsultaObject): {
  client: NormalizedClient;
  issues: ContractIssue[];
} {
  const issues: ContractIssue[] = [];
  const situacao = (consulta.Situacao || consulta.status || "").trim();

  // No MK, is_member() só aceita Situacao == "Ativo".
  if (situacao && !/ativo/i.test(situacao)) {
    issues.push({
      kind: "inativo",
      detail: `Cadastro do cliente está "${situacao}" na integração (precisa ser "Ativo").`,
    });
  }

  return {
    client: {
      name: consulta.Nome,
      document: consulta.CodigoPessoa
        ? `Código: ${consulta.CodigoPessoa}`
        : undefined,
      contact: joinUnique([consulta.Fone, consulta.Email]),
      address: joinUnique([consulta.Endereco, consulta.CEP]),
    },
    issues,
  };
}

function adaptSlugArrays(data: IntegrationData): NormalizedPlan[] {
  const groups: [string[] | undefined, string][] = [
    [data.combo_slugs, "combo_slugs"],
    [data.service_slugs, "service_slugs"],
    [data.products_slugs, "products_slugs"],
  ];

  const out: NormalizedPlan[] = [];
  groups.forEach(([slugs, source]) => {
    slugs?.forEach((slug) => {
      if (slug) out.push({ name: slug, source });
    });
  });
  return out;
}

/* ------------------------------------------------------------------ */

export function normalizeIntegrationData(
  data: IntegrationData | undefined,
  flags: AgreementMappingFlags = {}
): NormalizedIntegration {
  const contracts: NormalizedContract[] = [];
  const clients: NormalizedClient[] = [];
  const extraIssues: ContractIssue[] = [];
  let format: IntegrationFormat = "desconhecido";

  if (!data) {
    return { format, contracts, loosePlans: [], clients, issues: [] };
  }

  // Adapter / Voalle-Synsuite
  data.contratos?.forEach((contrato, index) => {
    format = "adapter";
    const { contract, client } = adaptAdapterContract(contrato, index);
    contracts.push(contract);
    if (client) clients.push(client);
  });

  // IXC — consulta como array
  if (Array.isArray(data.consulta)) {
    data.consulta.forEach((item, index) => {
      if (isIxcConsulta(item)) {
        format = "ixc";
        contracts.push(adaptIxcContract(item, index, flags));
        return;
      }

      // Consulta genérica em array (outras integrações)
      const name = item.contrato || item.descricao_aux_plano_venda;
      if (name) {
        contracts.push({
          key: item.id || `consulta-${index}`,
          label: `Consulta ${index + 1}`,
          plans: [{ name, id: item.id_vd_contrato, source: "Consulta" }],
          dates: [],
          issues: [],
          status: item.Situacao || item.status,
        });
      }
    });
  } else if (data.consulta) {
    // MK — consulta como objeto
    const { client, issues } = adaptConsultaObject(data.consulta);
    clients.push(client);
    extraIssues.push(...issues);
  }

  // MK — ContratosAtivos
  data.contrato?.forEach((item, index) => {
    if (format === "desconhecido") format = "mk";
    contracts.push(adaptMkContract(item, index));
  });

  const loosePlans = adaptSlugArrays(data);
  if (format === "desconhecido" && loosePlans.length) format = "slugs";

  const issues = [
    ...extraIssues,
    ...contracts.flatMap((contract) => contract.issues),
  ];

  return { format, contracts, loosePlans, clients, issues };
}

/** Todos os planos, de contratos e soltos */
export function collectNormalizedPlans(
  integration: NormalizedIntegration
): NormalizedPlan[] {
  return [
    ...integration.contracts.flatMap((contract) => contract.plans),
    ...integration.loosePlans,
  ];
}

/** Só os problemas que sozinhos explicam a falha (contrato inutilizável) */
export function getBlockingIssues(
  integration: NormalizedIntegration
): ContractIssue[] {
  const blocking: ContractIssueKind[] = [
    "vencido",
    "cancelado",
    "suspenso",
    "inativo",
  ];
  const seen = new Set<string>();

  return integration.issues.filter((issue) => {
    if (!blocking.includes(issue.kind)) return false;
    if (seen.has(issue.detail)) return false;
    seen.add(issue.detail);
    return true;
  });
}
